<template>
  <div class="vms-approval-groups-page">
    <VmsPageHeader
      kicker="Approvals"
      title="Approval Groups"
      subtitle="Assign people to process roles per location. Workflows reuse these roles — you do not attach groups into a workflow list."
      icon="mdi-account-multiple-check-outline"
    >
      <template #actions>
        <VmsColumnVisibility
          v-model="selectedHeaders"
          :headers="headers"
          @change="toggleColumnVisibility"
        />
        <v-text-field
          label="Search"
          v-model="filterReportBySearch"
          append-icon="mdi-magnify"
          dense
          outlined
          hide-details
          clearable
          color="primary"
          class="vms-toolbar-search"
        ></v-text-field>
        <v-btn color="primary" dark class="text-capitalize vms-primary-cta" @click="openCreate">
          <v-icon left>mdi-plus-thick</v-icon> New
        </v-btn>
      </template>
    </VmsPageHeader>

    <VmsResponsiveList :items="items" :loading="loading" loading-text="Loading approval groups…">
      <template #table>
        <v-data-table
          :headers="computedHeaders"
          :items="items"
          sort-by="title"
          class="elevation-0 vms-surface"
          :loading="loading"
          loading-text="Loading... Please wait"
          :search="filterReportBySearch"
          :custom-filter="tableSearchFilter"
          show-select
          :single-select="singleSelect"
          v-model="selected"
          :server-items-length="pagination?.total"
          :options.sync="options"
        >
          <template v-slot:[`item.title`]="{ item }">
            <div>
              <div class="font-weight-medium">{{ item.title }}</div>
              <div class="text-caption grey--text" v-if="item.role_label">{{ item.role_label }}</div>
            </div>
          </template>

          <template v-slot:[`item.organization_id`]="{ item }">
            <v-chip small :color="item.organization_id ? 'info' : 'primary'" dark :title="branchFullLabel(item)">
              <v-icon left x-small>
                {{ item.organization_id ? 'mdi-office-building-outline' : 'mdi-domain' }}
              </v-icon>
              {{ branchLabel(item) }}
            </v-chip>
          </template>

          <template v-slot:[`item.users`]="{ item }">
            <div class="my-1" v-if="item.users && item.users.length > 0">
              <v-btn v-for="(approver, i) in item.users" :key="`approver-${item.id || 'g'}-${approver.id || approver?.attributes?.id || i}`" color="teal" elevation="0" outlined class="mx-1 my-1"
                small>
                {{ approver?.attributes?.name || approver?.name }}
              </v-btn>
            </div>
            <div class="my-1" v-else>
              <v-btn color="error" elevation="0" outlined class="mx-1" small>
                No Approvers Found
              </v-btn>
            </div>
          </template>

          <template v-slot:no-data>
            <div class="py-8 text-center">
              <v-icon large color="grey lighten-1">mdi-account-multiple-plus-outline</v-icon>
              <p class="mt-2 grey--text">No approval groups yet. Create one group per role for HQ, and the same roles for each branch (Manager, Assistant Admin, Security).</p>
              <v-btn color="primary" class="text-capitalize" @click="openCreate">Create approval group</v-btn>
            </div>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <div class="vms-table-actions">
              <VmsIconAction
                icon="mdi-pencil-outline"
                label="Edit"
                @click="editItem(item)"
              />
              <VmsIconAction
                icon="mdi-delete-outline"
                label="Delete"
                color="error"
                @click="deleteItem(item)"
              />
            </div>
          </template>
        </v-data-table>
      </template>

      <template #card="{ item }">
        <div class="vms-list-card__title">{{ item.title }}</div>
        <div class="vms-list-card__meta">
          <v-chip small :color="item.organization_id ? 'info' : 'primary'" dark :title="branchFullLabel(item)">
            <v-icon left x-small>
              {{ item.organization_id ? 'mdi-office-building-outline' : 'mdi-domain' }}
            </v-icon>
            {{ branchLabel(item) }}
          </v-chip>
          <span>{{ (item.users && item.users.length) || 0 }} approver(s)</span>
        </div>
        <div class="vms-list-card__row" v-if="item.description">
          <span>{{ item.description }}</span>
        </div>
        <div class="vms-list-card__actions">
          <VmsIconAction
            icon="mdi-pencil-outline"
            label="Edit"
            color="grey"
            @click="editItem(item)"
          />
          <VmsIconAction
            icon="mdi-delete-outline"
            label="Delete"
            color="error"
            @click="deleteItem(item)"
          />
        </div>
      </template>

      <template #empty>
        <div class="vms-empty">
          <v-icon large color="grey lighten-1">mdi-account-multiple-plus-outline</v-icon>
          <p class="mt-2 grey--text">No approval groups yet. Create one group per role for HQ, and the same roles for each branch (Manager, Assistant Admin, Security).</p>
          <v-btn color="primary" class="text-capitalize" @click="openCreate">Create approval group</v-btn>
        </div>
      </template>
    </VmsResponsiveList>

    <v-dialog v-model="dialog" max-width="560px">
      <v-card>
        <v-form class="vms-form" ref="form" v-model="valid" lazy-validation>
          <v-card-title class="vms-form-title">
            {{ formTitle }}
          </v-card-title>
          <v-card-subtitle class="vms-form-subtitle">
            Assign people to each process role. For a new branch, create Manager, Assistant Administrator, and Branch Security groups — the shared Branch workflow picks them up automatically.
          </v-card-subtitle>
          <v-divider></v-divider>
          <v-card-text class="vms-form-card">
            <div class="vms-form-section">
              <span class="vms-form-section__label">Group details</span>
              <v-row>
                <v-col cols="12">
                  <v-select
                    v-model="editedItem.organization_id"
                    :items="branchOptions"
                    item-text="legal_name"
                    item-value="id"
                    label="Branch"
                    outlined
                    dense
                    hide-details="auto"
                    :rules="[(v) => v === 0 || !!v || 'Select HQ or a branch']"
                    @change="onScopeChange"
                  ></v-select>
                </v-col>
                <v-col cols="12">
                  <v-select
                    v-model="editedItem.role_key"
                    :items="roleOptions"
                    item-text="label"
                    item-value="role_key"
                    label="Process role"
                    outlined
                    dense
                    hide-details="auto"
                    :rules="[(v) => !!v || 'Select a role']"
                    @change="onRoleChange"
                  ></v-select>
                </v-col>
                <v-col cols="12">
                  <v-text-field 
                    v-model="editedItem.title" 
                    label="Title" 
                    :rules="rules.required_name"
                    dense
                    hide-details="auto"
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-textarea 
                    v-model="editedItem.description" 
                    outlined 
                    label="Description"
                    dense
                    hide-details="auto"
                    :rules="rules.required"
                    rows="2"
                    auto-grow
                  ></v-textarea>
                </v-col>
              </v-row>
            </div>
            <div class="vms-form-section">
              <span class="vms-form-section__label">Approvers</span>
              <v-row>
                <v-col cols="12">
                  <v-select 
                    v-model="editedItem.users" 
                    :items="filteredUsers"
                    label="Select approvers" 
                    item-text="attributes.name" 
                    item-value="id"
                    :no-data-text="usersLoading ? 'Loading users…' : 'No users in this organization'"
                    multiple 
                    outlined 
                    chips 
                    dense
                    hide-details="auto"
                    :loading="usersLoading"
                    :rules="rules.select2"
                  >
                    <template v-slot:prepend-item>
                      <v-list-item ripple @click="toggleRequired">
                        <v-list-item-action>
                          <v-icon :color="editedItem.users.length > 0 ? 'error' : ''">
                            {{ iconRequired }}
                          </v-icon>
                        </v-list-item-action>
                        <v-list-item-content>
                          <v-list-item-title>
                            Select All
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider class="mt-2"></v-divider>
                    </template>
                    <template v-slot:append-item>
                      <v-divider class="mb-2"></v-divider>
                      <v-list-item disabled>
                        <v-list-item-avatar color="grey lighten-3">
                          <v-icon> mdi-file-outline </v-icon>
                        </v-list-item-avatar>
                        <v-list-item-content v-if="likesAllUsers">
                          <v-list-item-title>
                            All Users selected for this group!
                          </v-list-item-title>
                        </v-list-item-content>

                        <v-list-item-content v-else-if="likesSomeUsers">
                          <v-list-item-title>
                            Users Count
                          </v-list-item-title>
                          <v-list-item-subtitle v-if="editedItem.users">
                            {{ editedItem.users.length }}
                          </v-list-item-subtitle>
                        </v-list-item-content>

                        <v-list-item-content v-else>
                          <v-list-item-title>
                            Which users are in this group?
                          </v-list-item-title>
                          <v-list-item-subtitle>
                            Go ahead, make a selection above!
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-select>
                </v-col>
              </v-row>
            </div>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions class="vms-form-actions">
            <v-btn text color="grey darken-1" class="text-capitalize" @click="close">
              Cancel
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary" class="text-capitalize white--text" :loading="loading" @click="save">
              Submit
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import ApprovalGroupService from "../../services/approval_group.service";
import OrganizationService from "../../services/organization.service";
import WorkflowService from "../../services/workflow.service";
import Swal from "sweetalert2";
import VmsPageHeader from "../../components/VmsPageHeader.vue";
import VmsResponsiveList from "../../components/VmsResponsiveList.vue";
import VmsIconAction from "../../components/VmsIconAction.vue";
import VmsColumnVisibility from "../../components/VmsColumnVisibility.vue";
import columnVisibility from "../../mixins/columnVisibility";

export default {
  components: {
    VmsPageHeader,
    VmsResponsiveList,
    VmsIconAction,
    VmsColumnVisibility,
  },
  mixins: [columnVisibility],
  data: () => ({
    name: "ApprovalGroup",
    users: [],
    organizations: [],
    hqRoles: [],
    branchRoles: [],
    usersLoading: false,
    /** Approver ids to restore after async user list load (edit race). */
    pendingApproverIds: [],
    user_ids: [],
    search: "",
    loading: true,
    items: [],
    groups: [],
    editedIndex: -1,
    editedItem: {
      title: "",
      description: "",
      role_key: null,
      users: [],
      user_ids: [],
      organization_id: 0,
    },
    defaultItem: {
      title: "",
      description: "",
      role_key: null,
      users: [],
      user_ids: [],
      organization_id: 0,
    },
    remember: 0,
    timeout: 2000,
    dialog: false,
    valid: true,
    rules: {
      select: [(v) => !!v || "An item should be selected"],
      select2: [(v) => v.length > 0 || "At least one user should be selected"],
      required: [
        (v) => !!v || "Field is required",
        (v) => (v && v.length >= 3) || "Min 3 characters",
      ],
      required_name: [
        (v) => !!v || "Name is required",
        (v) => (v && v.length >= 2) || "Min 2 characters",
      ],
      file: [
        (v) => !!v || "File is required",
        (v) => (v && v.length > 0) || "File is required",
      ],
    },
    headers: [
      {
        text: "#",
        align: "start",
        filterable: true,
        value: "id",
        visible: true,
      },
      {
        text: "Title",
        value: "title",
        visible: true,
      },
      {
        text: "Description",
        value: "description",
        visible: false,
      },
      {
        text: "Branch",
        value: "organization_id",
        visible: true,
      },
      {
        text: "Approvers",
        value: "users",
        visible: true,
      },
      {
        text: "Actions",
        align: "center",
        value: "actions",
        sortable: false,
        visible: true,
        sticky: true,
      },
    ],
    pagination: {
      page: 1,
      itemsPerPage: 10,
    },
    options: {},
    singleSelect: false,
    selected: [],
    filterReportBySearch: null,
    searchDebounce: null,
    search_term: null,
  }),
  computed: {
    formTitle() {
      return this.editedIndex === -1 ? "New" : "Edit";
    },
    loggedIn() {
      return this.$store.state.auth.status.loggedIn;
    },
    getLoading() {
      return this.loading;
    },
    branchOptions() {
      const opts = [
        { id: 0, legal_name: 'HQ' },
        ...this.organizations.map((org) => {
          const id = Number(org.id);
          const name =
            org.legal_name ||
            org.attributes?.legal_name ||
            org.name ||
            `Branch #${id}`;
          return {
            id,
            legal_name: this.shortenBranchName(name),
          };
        }),
      ];

      // Keep the group's current branch selectable even if not in memberships yet
      const currentId = Number(this.editedItem?.organization_id || 0);
      if (currentId && !opts.some((o) => Number(o.id) === currentId)) {
        const name =
          this.editedItem?.organization?.legal_name ||
          this.editedItem?.organization?.name ||
          `Branch #${currentId}`;
        opts.push({
          id: currentId,
          legal_name: this.shortenBranchName(name),
        });
      }

      return opts;
    },
    filteredUsers() {
      const selectedOrgId = Number(this.editedItem.organization_id || 0);
      const targetOrgIds = this.resolveTargetOrganizationIds(selectedOrgId);
      if (!targetOrgIds.length) {
        return [];
      }

      return this.users.filter((user) => {
        const isAdmin = Number(user.is_admin ?? user.attributes?.is_admin ?? 0) === 1;
        if (!isAdmin) return false;

        const memberships = this.userOrganizationIds(user);
        if (!memberships.length) return false;
        return targetOrgIds.some((id) => memberships.includes(id));
      });
    },
    roleOptions() {
      return this.editedItem.organization_id === 0 || !this.editedItem.organization_id
        ? this.hqRoles
        : this.branchRoles;
    },
    likesAllUsers() {
      return this.editedItem.users.length === this.filteredUsers.length;
    },
    likesSomeUsers() {
      return this.editedItem.users.length > 0 && !this.likesAllUsers;
    },
    iconRequired() {
      if (this.likesAllUsers) return "mdi-close-box";
      if (this.likesSomeUsers) return "mdi-minus-box";
      return "mdi-checkbox-blank-outline";
    },
  },
  watch: {
    dialog(val) {
      val || this.close();
    },
    options: {
      handler () {
        this.index();
      },
      deep: true,
    },
    filterReportBySearch(val) {
      clearTimeout(this.searchDebounce);

      this.searchDebounce = setTimeout(() => {
        this.search_term = (val || '').trim();

        this.options = {
          ...this.options,
          page: 1,
        };

        this.index();
      }, 300);
    },
  },
  created() {
    this.initColumnVisibility();
    this.loadRoles().then(() => {
      this.index();
      this.getOrganizations();
    });
  },
  methods: {
    openCreate() {
      this.editedIndex = -1;
      this.pendingApproverIds = [];
      this.editedItem = Object.assign({}, this.defaultItem, {
        title: '',
        description: '',
        role_key: null,
        users: [],
        organization_id: 0,
      });
      this.loadUsersForSelectedOrganization();
      this.dialog = true;
    },
    normalizeUserIds(value) {
      const list = Array.isArray(value) ? value : [];
      return [
        ...new Set(
          list
            .map((entry) => Number(entry?.id ?? entry))
            .filter((id) => Number.isFinite(id) && id > 0)
        ),
      ];
    },
    /**
     * Keep v-select selection in sync with filteredUsers.
     * Uses Number ids so item-value matches organization_users payloads (string ids).
     * Does not clear selection while the user list is still empty/loading.
     */
    syncApproverSelection(preferredIds) {
      const ids = this.normalizeUserIds(
        preferredIds != null ? preferredIds : this.editedItem.users
      );
      if (this.usersLoading) {
        this.editedItem.users = ids;
        return;
      }

      const allowed = new Set(this.filteredUsers.map((u) => Number(u.id)));
      if (!allowed.size) {
        // List not ready yet (e.g. orgs still loading) — keep intended selection
        this.editedItem.users = ids;
        return;
      }

      this.editedItem.users = ids.filter((id) => allowed.has(id));
    },
    branchFullLabel(item) {
      if (!item.organization_id) return 'HQ / Company-wide';
      return item.organization?.legal_name || item.organization?.name || ('Branch #' + item.organization_id);
    },
    branchLabel(item) {
      if (item.organization_label) {
        return this.shortenBranchName(item.organization_label);
      }
      if (!item.organization_id) return 'HQ';
      const raw = item.organization?.legal_name || item.organization?.name || ('Branch #' + item.organization_id);
      return this.shortenBranchName(raw);
    },
    shortenBranchName(name) {
      if (!name) return '';
      let short = String(name).split('@')[0].trim();
      // Drop common legal suffixes to keep chips readable
      short = short
        .replace(/\b(limited|ltd\.?|plc\.?|inc\.?|corp\.?|corporation|company)\b/gi, '')
        .replace(/\s{2,}/g, ' ')
        .trim();
      if (short.length > 28) {
        short = short.slice(0, 26).trimEnd() + '…';
      }
      return short || String(name).split('@')[0].trim();
    },
    onBranchChange() {
      this.syncApproverSelection(this.pendingApproverIds.length
        ? this.pendingApproverIds
        : this.editedItem.users);
    },
    onScopeChange() {
      // Branch change is intentional — drop previous pending edit selection
      this.pendingApproverIds = [];
      this.loadUsersForSelectedOrganization().then(() => {
        this.onBranchChange();
      });
      const allowedKeys = new Set(this.roleOptions.map((r) => r.role_key));
      if (this.editedItem.role_key && !allowedKeys.has(this.editedItem.role_key)) {
        this.editedItem.role_key = null;
        this.editedItem.title = '';
        this.editedItem.description = '';
      }
    },
    userOrganizationIds(user) {
      const fromPivot = (user.user_organizations || []).map((id) => Number(id));
      const fromOrgs = (user.organisations || user.attributes?.organisations || [])
        .map((o) => Number(o.id || o.org_id));
      return [...new Set([...fromPivot, ...fromOrgs].filter((id) => !!id))];
    },
    resolveTargetOrganizationIds(selectedOrgId) {
      if (selectedOrgId) {
        return [Number(selectedOrgId)];
      }

      // HQ / company-wide: parent organizations only
      const parents = this.organizations
        .filter((org) => {
          const parentId = org.id_parent ?? org.attributes?.id_parent;
          const isParent =
            org.isParentOrganization === true ||
            org.attributes?.isParentOrganization === true ||
            parentId == null ||
            parentId === '' ||
            Number(parentId) === 0;
          return isParent;
        })
        .map((org) => Number(org.id || org.attributes?.id))
        .filter((id) => !!id);

      if (parents.length) {
        return [...new Set(parents)];
      }

      const stored = JSON.parse(window.localStorage.getItem('user') || '{}');
      const fallback = Number(stored?.organization?.id || 0);
      return fallback ? [fallback] : [];
    },
    loadUsersForSelectedOrganization() {
      const selectedOrgId = Number(this.editedItem.organization_id || 0);
      const targetOrgIds = this.resolveTargetOrganizationIds(selectedOrgId);

      if (!targetOrgIds.length) {
        this.users = [];
        return Promise.resolve([]);
      }

      this.usersLoading = true;
      // Load members of each target org from user_organizations (active memberships)
      const requests = targetOrgIds.map((orgId) =>
        OrganizationService.organization_users(orgId, {
          options: { page: 1, itemsPerPage: -1, filter: '' },
          adminsOnly: true,
        })
      );

      return Promise.all(requests)
        .then((responses) => {
          const byId = new Map();
          responses.forEach((response) => {
            const rows = response?.data?.data?.data || response?.data?.data || [];
            (Array.isArray(rows) ? rows : []).forEach((user) => {
              const id = Number(user.id);
              if (!id || byId.has(id)) return;
              // Normalize id to Number so v-select item-value matches editedItem.users
              byId.set(id, { ...user, id });
            });
          });
          this.users = Array.from(byId.values());
          return this.users;
        })
        .catch((error) => {
          console.log(error);
          this.users = [];
          return [];
        })
        .finally(() => {
          this.usersLoading = false;
          if (this.dialog) {
            this.syncApproverSelection(
              this.pendingApproverIds.length
                ? this.pendingApproverIds
                : this.editedItem.users
            );
          }
        });
    },
    onRoleChange(roleKey) {
      const role = this.roleOptions.find((r) => r.role_key === roleKey);
      if (!role) return;
      if (!this.editedItem.title || this.editedIndex === -1) {
        this.editedItem.title = role.short || role.label;
      }
      if (!this.editedItem.description || this.editedIndex === -1) {
        this.editedItem.description = role.description || role.label;
      }
    },
    loadRoles() {
      return Promise.all([
        WorkflowService.getApprovalRoles('hq'),
        WorkflowService.getApprovalRoles('branch'),
      ]).then(([hqRes, branchRes]) => {
        this.hqRoles = hqRes?.data?.data || [];
        this.branchRoles = branchRes?.data?.data || [];
      });
    },
    // required docs
    toggleRequired() {
      this.$nextTick(() => {
        if (this.likesAllUsers) {
          this.editedItem.users = [];
          this.pendingApproverIds = [];
        } else {
          this.editedItem.users = this.filteredUsers.map((user) => Number(user.id));
          this.pendingApproverIds = [...this.editedItem.users];
        }
      });
    },
    getOrganizations() {
      const user = JSON.parse(window.localStorage.getItem('user') || '{}');
      if (!user?.id) {
        this.organizations = user?.organisations || [];
        if (this.dialog) this.loadUsersForSelectedOrganization();
        return;
      }
      OrganizationService.personOrganizations(user.id).then(
        (response) => {
          if (response.status == 200) {
            this.organizations = response.data.data || [];
          }
          // HQ filtering depends on parent orgs — reload users if dialog is open
          if (this.dialog) this.loadUsersForSelectedOrganization();
        },
        () => {
          this.organizations = user?.organisations || [];
          if (this.dialog) this.loadUsersForSelectedOrganization();
        }
      );
    },
    // search
    filterText(value, search) {
      return (
        value != null &&
        search != null &&
        typeof value === "string" &&
        value.toString().toLocaleLowerCase().indexOf(search) !== -1
      );
    },
    getNestedValue(obj, path) {
      if (!path) return undefined;
      return String(path).split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
    },
    tableSearchFilter(value, search, item) {
      if (!search) return true;
      const term = String(search).toLowerCase().trim();

      return this.headers.some((header) => {
        const v = this.getNestedValue(item, header.value);
        return v !== null && v !== undefined && String(v).toLowerCase().includes(term);
      });
    },
    handleLogout() {
      this.$store.dispatch("auth/logout");
      this.$router.push("/login");
    },
    validate() {
      this.$refs.form.validate();
    },
    reset() {
      this.$refs.form.reset();
    },
    resetValidation() {
      this.$refs.form.resetValidation();
    },
    index() {
      return ApprovalGroupService.index({
        options: this.options,
        search: this.search_term ?? this.filterReportBySearch,
      }).then(
        (response) => {
          if (response.status == 200) {
            this.items = response?.data?.data?.data;
            this.pagination = response?.data?.data;
            this.loading = false;
          } else {
            this.items = [];
          }
        },
        (error) => {
          this.items = [];
          console.log(error);
          if (error.response.status == 401) {
            this.handleLogout();
          }
        }
      );
    },
    editItem(item) {
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = Object.assign({}, item);
      // Normalize to Number so v-select matches branchOptions ids (API org ids are strings)
      this.editedItem.organization_id =
        item.organization_id == null || item.organization_id === ''
          ? 0
          : Number(item.organization_id);

      // Prefer user_ids; fall back to nested users[].id (API often returns string ids)
      const selectedIds = this.normalizeUserIds(
        Array.isArray(item.user_ids) && item.user_ids.length
          ? item.user_ids
          : item.users
      );
      this.pendingApproverIds = selectedIds;
      this.editedItem.users = selectedIds;

      this.loadUsersForSelectedOrganization().then(() => {
        this.syncApproverSelection(selectedIds);
      });
      this.dialog = true;
    },

    buildGroupPayload() {
      const orgId = this.editedItem.organization_id;
      return {
        id: this.editedItem.id,
        title: this.editedItem.title,
        description: this.editedItem.description,
        status: !!this.editedItem.status,
        organization_id: orgId === 0 || orgId === '0' || orgId === '' || orgId == null ? null : Number(orgId),
        role_key: this.editedItem.role_key,
        users: this.normalizeUserIds(this.editedItem.users),
      };
    },

    deleteItem(item) {
      const index = this.items.indexOf(item);
      Swal.fire({
        title: "Are you sure you want to delete this group?",
        text: "You cannot undo this action",
        icon: "warning",
        showCancelButton: true,
      }).then((result) => {
        if (result.isConfirmed && this.items.splice(index, 1)) {
          ApprovalGroupService.delete(item).then(
            (response) => {
              if (response.status == 200) {
                this.$store.dispatch("alert/success", response.data.message);
                this.close();
              } else {
                this.$store.dispatch("alert/error", response.data.message);
              }
            },
            (error) => {
              if (error.response.status == 422) {
                this.$store.dispatch(
                  "alert/error",
                  error.response.data.message
                );
              }
              if (error.response.status == 401) {
                this.handleLogout();
              }
              console.log(error);
              this.$store.dispatch("alert/error", error.response.data.message);
            }
          );
        }
      });
    },

    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
        this.pendingApproverIds = [];
      });
    },

    getBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
    },

    save() {
      this.loading = true;
      this.$refs.form.validate();
      if (this.valid) {
        const payload = this.buildGroupPayload();
        // edit
        if (this.editedIndex > -1) {
          ApprovalGroupService.update(payload).then(
            (response) => {
              if (response.status == 200) {
                this.$store.dispatch("alert/success", response.data.message);
                this.loading = false;
                this.index();
                this.close();
              } else {
                this.$store.dispatch("alert/error", response.data.message);
                this.index();
                this.loading = false;
              }
            },
            (error) => {
              const status = error.response?.status;
              const message =
                error.response?.data?.message ||
                error.message ||
                "Could not update approval group";
              if (status == 422) {
                this.$store.dispatch("alert/error", message);
                this.loading = false;
                this.index();
              }
              if (status == 401) {
                this.handleLogout();
                this.loading = false;
                this.index();
              }
              console.log(error);
              this.$store.dispatch("alert/error", message);
              this.loading = false;
            }
          );
        } else {
          // create
          ApprovalGroupService.store(payload).then(
            (response) => {
              if (response.status == 200) {
                this.items.push(this.editedItem);
                this.$store.dispatch(
                  "alert/success",
                  "Item created successfully"
                );
                this.loading = false;
                this.index();
                this.close();
              } else {
                this.$store.dispatch("alert/error", response.data.message);
                this.index();
              }
            },
            (error) => {
              const status = error.response?.status;
              const message =
                error.response?.data?.message ||
                error.message ||
                "Could not create approval group";
              if (status == 422) {
                this.$store.dispatch("alert/error", message);
                this.loading = false;
                this.index();
              }
              if (status == 401) {
                this.handleLogout();
                this.loading = false;
                this.index();
              }
              console.log(error);
              this.$store.dispatch("alert/error", message);
              this.loading = false;
            }
          );
        }
      } else {
        this.loading = false;
        this.index();
      }
    },
  },
};
</script>
